import React, { useContext, useCallback } from 'react';
import BikeContext from "../Components/BikeContext";
import useToken from "../Components/useToken";
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation, getI18n } from 'react-i18next';
import Seo from '../Components/Seo';

/**
 * BikeDetails component.
 * 
 * This component displays the details of a selected bike. The bike's details include its image, year, brand, model, tire size, and price.
 * The bike's id is obtained from the current URL, and the bike's details are fetched from the BikeContext.
 * 
 * The user can also navigate to the edit bike page by clicking the 'Edit' button.
 * 
 * State:
 * - token: The user's authentication token. Can be a string or null.
 * - setToken: Function to update the token state.
 * 
 * Functions:
 * - editBike: Navigates to the edit bike page.
 * 
 * @returns The BikeDetails component.
 */

export default function BikeDetails() {
    const { t } = useTranslation();
    const { id } = useParams();

    const { token } = useToken();

    const navigate = useNavigate();

    const editBike = useCallback((id: number) => navigate('/editbike/' + id, { replace: true }), [navigate]);

    const bikes = useContext(BikeContext).bikes;
    const bikeId = Number(id);
    const selectedBike = bikes.find(bike => bike.id === bikeId);
    const bikesLoaded = bikes.length > 0;
    const bikePath = id ? `/bike/${id}` : '/products';
    const imageUrl = selectedBike
        ? 'https://bikes.blob.core.windows.net/bikeimages/' + selectedBike.id + '.jpg'
        : '/logo192.png';

    const currentLanguage = getI18n().language;
    var notes = (currentLanguage === 'en' ? selectedBike?.notesEn : selectedBike?.notes) || "";
    const title = selectedBike
        ? `${selectedBike.year || ''} ${selectedBike.brand || ''} ${selectedBike.model || ''}${selectedBike.tireSize ? ` ${selectedBike.tireSize}3` : ''}`.trim()
        : 'Polkupyörän tiedot';
    const description = selectedBike
        ? `${selectedBike.brand || 'Polkupyörä'} ${selectedBike.model || ''} ${selectedBike.year || ''}, rengaskoko ${selectedBike.tireSize || '-'}\u2033 ja hinta ${selectedBike.price || 0} euroa. Katso pyörän tiedot Kempeleen Pyöräpajalta.`.replace(/\s+/g, ' ').trim()
        : 'Tämän polkupyörän tietoja ei löytynyt. Sivua ei pidetä indeksoitavana.';

    return (
        <div className="BikeDetails">
            {selectedBike ? (
                <Seo
                    title={title}
                    description={description}
                    path={bikePath}
                    robots="index,follow,max-image-preview:large"
                    imagePath={imageUrl}
                />
            ) : bikesLoaded ? (
                <Seo
                    title="Polkupyörän tiedot"
                    description="Tämän polkupyörän tietoja ei löytynyt. Sivua ei pidetä indeksoitavana."
                    path={bikePath}
                    robots="noindex,nofollow"
                    imagePath="/logo192.png"
                />
            ) : null}
            <div className='Content'>
                <div className='BikeInfo'>
                    <img src={imageUrl} className="bike-full-image" alt={selectedBike ? `${selectedBike.brand || 'Polkupyörä'} ${selectedBike.model || ''}`.trim() : 'Polkupyörän kuva'} width="800" height="600" />
                    <h1>{selectedBike?.year} {selectedBike?.brand} {selectedBike?.model} {selectedBike?.tireSize}″</h1>
                    <p className="bike-price">{t('bikeDetails.price')}: {selectedBike?.price}€</p>
                    <h2>{t('bikeDetails.additionalInfo')}</h2>
                    <p className="bike-description">{notes}</p>
                </div>
            </div>
            <Link to='/products' className='backbutton'> {t('bikeDetails.back')}</Link>
            {token ? <button className="adminbutton" onClick={() => editBike(selectedBike?.id || 1)}>Muokkaa</button> : null}
        </div>
    )
}

